import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { FadeUp, SectionHead } from "@/components/Reveal";
import { useQuery } from "@tanstack/react-query";
import { useLang } from "@/lib/i18n";

const CATS = [
  { key: "all", zh: "全部", en: "ALL" },
  { key: "concert", zh: "演唱会", en: "CONCERTS" },
  { key: "festival", zh: "音乐节", en: "FESTIVALS" },
  { key: "corporate", zh: "商业活动", en: "CORPORATE" },
  { key: "production", zh: "音乐制作", en: "PRODUCTION" },
];

const CAT_LABEL = { concert: "演唱会", festival: "音乐节", corporate: "商业活动", production: "音乐制作" };
const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-5", "md:col-span-7"];
const FALLBACK_IMG = "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?q=80&w=1600&auto=format&fit=crop";

function ProjectCard({ p, i }) {
  const span = SPANS[i % SPANS.length];
  const navigate = useNavigate();
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`project-card-${i}`}
      onClick={() => p.id && navigate(`/works/${p.id}`)}
      className={`group relative overflow-hidden border border-white/10 hover:border-[#ffde00] transition-colors duration-300 cursor-pointer ${span}`}
    >
      <div className={`w-full overflow-hidden ${span === "md:col-span-4" ? "h-[300px] md:h-[380px]" : "h-[320px] md:h-[460px]"}`}>
        <img
          src={p.image || FALLBACK_IMG}
          alt={p.title}
          loading="lazy"
          className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition duration-700 ease-out"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="font-mono2 text-[10px] md:text-xs text-[#ffde00] tracking-[0.25em]">
            {p.year || "—"} · {p.location || CAT_LABEL[p.category] || ""}
          </div>
          <h3 className="font-display text-xl md:text-2xl uppercase leading-tight mt-1.5 text-[#f5f5f5] truncate">
            {p.title}
          </h3>
          <p className="font-zh text-[11px] text-[#a1a1aa] mt-1.5 hidden md:block">{p.role}</p>
        </div>
        <ArrowUpRight size={22} className="shrink-0 text-[#ffde00] opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300" />
      </div>
    </motion.article>
  );
}

export default function Portfolio() {
  const [filter, setFilter] = useState("all");
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => api.get("/projects").then((r) => r.data),
    staleTime: 60_000,
  });

  const filtered = useMemo(
    () => (filter === "all" ? projects : projects.filter((p) => p.category === filter)),
    [projects, filter]
  );

  return (
    <section id="works" data-testid="works" className="py-24 md:py-32 bg-[#0a0a0a]">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8">
        <SectionHead
          no="02"
          en="SELECTED WORKS"
          zh="演出作品"
          right={
            <FadeUp className="font-mono2 text-xs text-[#a1a1aa] tracking-[0.25em] whitespace-nowrap">
              {filtered.length} PROJECTS
            </FadeUp>
          }
        />
        <div className="flex flex-wrap gap-2 mb-10 md:mb-14">
          {CATS.map((c) => (
            <button
              key={c.key}
              data-testid={`works-filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`px-4 md:px-6 py-2.5 font-mono2 text-[11px] tracking-[0.2em] border cursor-pointer transition-colors duration-200 ${
                filter === c.key
                  ? "bg-[#ffde00] text-black border-[#ffde00]"
                  : "bg-transparent text-[#a1a1aa] border-white/15 hover:border-[#ffde00] hover:text-[#ffde00]"
              }`}
            >
              {c.zh} · {c.en}
            </button>
          ))}
        </div>
        <div key={filter} className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {filtered.map((p, i) => (
            <ProjectCard key={p.id || p.title + i} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
